'use client';

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { EDITOR_PREVIEW_CSS, SECTION_GUIDE_PREVIEW_CSS } from '../../utils/editorPreviewStyles';

/**
 * Section guide (read-only sample) vs full-page preview of current section HTML.
 * SOW: single dismiss control (X); titles "Section guide: …" / "Preview: …"; optional subtext.
 */
export default function EditorSectionPreviewModal({
	isOpen,
	onClose,
	title,
	subtitle,
	html,
	variant = 'guide', // 'guide' | 'preview'
	/** When set with variant "guide", shows Guidance vs Example tabs (SOW: example separate from guidance). */
	guideExampleHtml = null
}) {
	const [guideTab, setGuideTab] = useState('guidance');

	useEffect(() => {
		if (isOpen) setGuideTab('guidance');
	}, [isOpen]);

	if (!isOpen) return null;

	const showGuideTabs = variant === 'guide' && guideExampleHtml;
	const bodyHtml = showGuideTabs && guideTab === 'example' ? guideExampleHtml : html;
	const previewCss = variant === 'preview' ? EDITOR_PREVIEW_CSS : SECTION_GUIDE_PREVIEW_CSS;

	const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${previewCss}</style></head><body>${bodyHtml || '<p></p>'}</body></html>`;

	return (
		<div className="fixed inset-0 z-[10050] flex items-center justify-center p-4">
			<button
				type="button"
				className="absolute inset-0 bg-black/50"
				aria-label="Close"
				onClick={onClose}
			/>
			<div
				className="relative z-[10051] flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl"
				role="dialog"
				aria-modal="true"
				aria-labelledby="section-preview-modal-title"
			>
				<div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
					<div className="min-w-0 pr-8">
						<h2 id="section-preview-modal-title" className="text-lg font-semibold text-dark-blue">
							{title}
						</h2>
						{subtitle ? (
							<p className="mt-1 text-sm text-gray-500">{subtitle}</p>
						) : null}
					</div>
					<button
						type="button"
						onClick={onClose}
						className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-dark-blue"
						aria-label="Close"
					>
						<X className="h-5 w-5" />
					</button>
				</div>
				{showGuideTabs ? (
					<div className="flex gap-2 border-b border-gray-200 px-6 pt-2">
						<button
							type="button"
							onClick={() => setGuideTab('guidance')}
							className={`rounded-t-lg px-4 py-2 text-sm font-medium ${
								guideTab === 'guidance'
									? 'bg-white text-dark-blue border border-b-0 border-gray-200 -mb-px'
									: 'text-gray-600 hover:text-dark-blue'
							}`}
						>
							Guidance
						</button>
						<button
							type="button"
							onClick={() => setGuideTab('example')}
							className={`rounded-t-lg px-4 py-2 text-sm font-medium ${
								guideTab === 'example'
									? 'bg-white text-dark-blue border border-b-0 border-gray-200 -mb-px'
									: 'text-gray-600 hover:text-dark-blue'
							}`}
						>
							Example
						</button>
					</div>
				) : null}
				<div className="min-h-[200px] flex-1 overflow-hidden p-4">
					<iframe
						title={variant === 'guide' ? 'Section guide' : 'Section preview'}
						key={showGuideTabs ? guideTab : 'single'}
						className="h-[min(70vh,720px)] w-full rounded border border-gray-200 bg-white"
						sandbox="allow-same-origin"
						srcDoc={docHtml}
					/>
				</div>
			</div>
		</div>
	);
}
